import React from "react";
import { ConfirmModalProps } from "./ConfirmModal.types";
import { Modal } from "../../../design-system/components/composite/Modal/Modal";
import { InfoCircleIcon } from "../../atoms";
import {
  ModalBody,
  ModalTitle,
  ModalDescription,
  InfoBox,
  InfoIcon,
  InfoText,
  ModalFooter,
  CancelButton,
  ConfirmButton,
} from "./ConfirmModal.styles";

const ConfirmModalBase = ({
  open,
  isLoading = false,
  title,
  description,
  infoText,
  infoVariant = "info",
  onConfirm,
  onCancel,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "primary",
  confirmDisabled = false,
  zIndex,
}: ConfirmModalProps) => {
  const loading = isLoading;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="md"
      zIndex={zIndex ?? 1400}
      closeOnBackdropClick={!loading}
      closeOnEscape={!loading}
      closable={!loading}
    >
      <ModalBody>
        <ModalTitle id="modal-title">{title}</ModalTitle>
        <ModalDescription id="modal-description">
          {description}
        </ModalDescription>

        {infoText && (
          <InfoBox $variant={infoVariant}>
            <InfoIcon $variant={infoVariant}>
              {infoVariant === "warning" ? (
                <svg fill="none" viewBox="0 0 20 20">
                  <path
                    d="M10.0008 7.50016V10.8335M10.0008 14.1668H10.0091M8.57493 3.21683L1.5166 15.0002C1.37113 15.2521 1.29416 15.5376 1.2933 15.8284C1.29243 16.1193 1.3677 16.4052 1.51167 16.658C1.65564 16.9108 1.86329 17.1218 2.11372 17.2698C2.36414 17.4178 2.64845 17.4975 2.9386 17.5002H17.0633C17.3535 17.4975 17.6378 17.4178 17.8882 17.2698C18.1386 17.1218 18.3463 16.9108 18.4903 16.658C18.6342 16.4052 18.7095 16.1193 18.7086 15.8284C18.7078 15.5376 18.6308 15.2521 18.4853 15.0002L11.427 3.21683C11.2789 2.972 11.0704 2.769 10.8215 2.62726C10.5725 2.48553 10.2913 2.40991 10.0049 2.40771C9.71839 2.40551 9.43603 2.4768 9.18492 2.61469C8.93381 2.75258 8.7222 2.95236 8.57093 3.19489L8.57493 3.21683Z"
                    stroke="currentColor"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <InfoCircleIcon size={20} color="currentColor" />
              )}
            </InfoIcon>
            <InfoText>{infoText}</InfoText>
          </InfoBox>
        )}
      </ModalBody>

      <ModalFooter>
        <CancelButton
          onClick={onCancel}
          disabled={loading}
          aria-label={cancelLabel}
        >
          {cancelLabel}
        </CancelButton>

        <ConfirmButton
          $variant={confirmVariant}
          onClick={onConfirm}
          disabled={loading || confirmDisabled}
          aria-label={confirmLabel}
        >
          {loading && (
            <svg
              className="confirm-modal-spinner"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                opacity="0.25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                opacity="0.75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          {confirmLabel}
        </ConfirmButton>
      </ModalFooter>
    </Modal>
  );
};

export const ConfirmModal = React.memo(ConfirmModalBase);
export default ConfirmModal;
